import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "remixicon/fonts/remixicon.css";
import "bootstrap/dist/css/bootstrap.min.css";

const UsersList = ({ users = [], onEdit, onDelete, onAddUser }) => {
  const [filterText, setFilterText] = useState("");
  const [showExport, setShowExport] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(filterText.toLowerCase()) ||
      user.email.toLowerCase().includes(filterText.toLowerCase())
  );

  const totalRows = filteredUsers.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // ✅ Custom Pagination Component - Same as RolesList
  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);

    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        {/* Left: Rows per page - With original dropdown styling */}
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px", fontSize: "0.875rem" }}
            value={rowsPerPage}
            onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}
          >
            {[5, 10, 15, 20, 25].map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Right: Pagination buttons with original styling */}
        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            style={{
              borderRadius: "6px",
              height: "32px",
              width: "32px",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#495057",
              border: "1px solid #dee2e6",
              backgroundColor: "#fff",
            }}
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className="btn btn-sm border"
            style={{
              borderRadius: "6px",
              height: "32px",
              width: "32px",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#495057",
              border: "1px solid #dee2e6",
              backgroundColor: "#fff",
            }}
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  const columns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "80px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Roles",
      cell: (row) =>
        row.roles && row.roles.length > 0 ? (
          row.roles.map((role) => (
            <span key={role.id} className="badge bg-primary me-1">
              {role.name}
            </span>
          ))
        ) : (
          <span className="text-muted">No roles</span>
        ),
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`badge ${row.status === "inactive" ? "bg-danger" : "bg-success"}`}>
          {row.status === "inactive" ? "Inactive" : "Active"}
        </span>
      ),
      sortable: true,
      selector: (row) => row.status || "active",
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          {onEdit && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Edit User"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-edit-line"></i>
            </button>
          )}
          {onDelete && (
            <button
              className="btn btn-sm btn-outline-danger"
              title="Delete"
              onClick={() => {
                onDelete(row.id);
              }}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  // ✅ Export Handlers
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Users List", 14, 10);
    const tableColumn = ["ID", "Name", "Email", "Roles", "Status"];
    const tableRows = [];

    filteredUsers.forEach((user) => {
      const roles =
        user.roles && user.roles.length > 0
          ? user.roles.map((r) => r.name).join(", ")
          : "No roles";
      const statusText = user.status === "inactive" ? "Inactive" : "Active";
      tableRows.push([user.id, user.name, user.email, roles, statusText]);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save("users_list.pdf");
  };

  const handleCopy = () => {
    const copyText = filteredUsers
      .map((u) => `${u.id}\t${u.name}\t${u.email}\t${u.status === "inactive" ? "Inactive" : "Active"}`)
      .join("\n");

    navigator.clipboard.writeText(copyText)
      .then(() => {
        Swal.fire({
          title: "Copied!",
          text: "User data has been copied to clipboard.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          position: "top",
          width: "280px",
        });
      })
      .catch(() => {
        Swal.fire({
          title: "Error!",
          text: "Failed to copy data. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
          position: "top",
        });
      });
  };

  const handlePrint = () => {
    const printContent = `
      <html>
      <head>
        <title>Users List</title>
        <style>
          body { font-family: Arial; margin: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 6px; text-align: left; }
          th { background: #f8f9fa; }
        </style>
      </head>
      <body>
        <h3>Users List</h3>
        <table>
          <thead>
            <tr><th>ID</th><th>Name</th><th>Email</th><th>Roles</th><th>Status</th></tr>
          </thead>
          <tbody>
            ${filteredUsers
        .map(
          (u) => `
              <tr>
                <td>${u.id}</td>
                <td>${u.name}</td>
                <td>${u.email}</td>
                <td>${u.roles && u.roles.length > 0
              ? u.roles.map((r) => r.name).join(", ")
              : "No roles"
            }</td>
                <td>${u.status === "inactive" ? "Inactive" : "Active"}</td>
              </tr>`
        )
        .join("")}
          </tbody>
        </table>
      </body>
      </html>`;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const customStyles = {
    pagination: {
      style: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderTop: "1px solid #dee2e6",
        padding: "10px 20px",
      },
      pageButtonsStyle: {
        borderRadius: "6px",
        height: "32px",
        padding: "0 10px",
        margin: "0 4px",
        cursor: "pointer",
        color: "#495057",
        border: "1px solid #dee2e6",
        backgroundColor: "#fff",
        "&:disabled": {
          opacity: 0.4,
          cursor: "not-allowed",
        },
        "&:hover:not(:disabled)": {
          backgroundColor: "#f1f3f5",
        },
      },
    },
  };

  return (
    <div>
      <div>
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
          {/* Left: Export + Search */}
          <div className="d-flex align-items-center gap-2 position-relative">
            <div className="dropdown">
              <button
                className="btn btn-label-primary btn-sm dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ri-download-2-line me-1"></i> Export
              </button>
              <ul className="dropdown-menu shadow-sm">
                <li>
                  <button className="dropdown-item" onClick={handleExportPDF}>
                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                  </button>
                </li>
                <li>
                  <CSVLink
                    data={filteredUsers}
                    filename="users_export.csv"
                    className="dropdown-item"
                  >
                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                  </CSVLink>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleCopy}>
                    <i className="ri-file-copy-line me-1"></i> Copy
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handlePrint}>
                    <i className="ri-printer-line me-1"></i> Print
                  </button>
                </li>
              </ul>
            </div>

            <input
              type="text"
              placeholder="Search user..."
              className="form-control form-control-sm"
              style={{ width: "200px" }}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>

          {/* Center: Info */}
          <h6 className="mb-0 text-center text-muted flex-grow-1">
            Showing {start}-{end} of {totalRows} users
          </h6>

          {/* Right: Add Button */}
          <div>
            {onAddUser && (
              <button className="btn btn-primary btn-sm" onClick={onAddUser}>
                <i className="ri-user-add-line me-1"></i> Add User
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="card-body p-2">
          <DataTable
            columns={columns}
            data={filteredUsers}
            pagination
            paginationPerPage={rowsPerPage}
            paginationTotalRows={totalRows}
            onChangeRowsPerPage={setRowsPerPage}
            onChangePage={setCurrentPage}
            paginationComponent={CustomPagination}
            highlightOnHover
            striped
            responsive
            customStyles={customStyles}
            noDataComponent={<div className="p-3 text-muted">No users found</div>}
          />
        </div>
      </div>
    </div>
  );
};

export default UsersList;